// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { forwardRef, useImperativeHandle } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAskHelios, SUGGESTED_QUESTIONS } from '../../hooks/useAskHelios';
import ChatInput from './ChatInput';
import type { AskHeliosMessage } from '../../types';
import './AskHeliosChat.css';

const CONFIDENCE_LABEL: Record<string, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

/** Human labels for the route the answer came from. */
const ROUTE_LABEL: Record<string, string> = {
  orchestrator: 'Orchestrator',
  'sop-librarian': 'SOP Librarian',
  'knowledge-graph': 'Knowledge Graph',
  'advisory-boundary': 'Advisory boundary',
  refusal: 'Advisory boundary',
};

function AssistantMeta({ message }: { message: AskHeliosMessage }) {
  const showConfidence = !!message.confidence;
  const showCitations = (message.citations?.length ?? 0) > 0;
  const showChips = (message.contextChips?.length ?? 0) > 0;
  const routeLabel = message.route ? (ROUTE_LABEL[message.route] ?? message.route) : null;
  if (!showConfidence && !showCitations && !showChips && !message.disclaimer) return null;

  return (
    <div className="akh-meta">
      {showChips && (
        <ul className="akh-context-chips" aria-label="Context">
          {message.contextChips!.map((chip, i) => (
            <li key={`${chip.type}-${i}`} className={`akh-ctx-chip akh-ctx-${chip.type}`}>
              {chip.label}
            </li>
          ))}
        </ul>
      )}

      {showConfidence && (
        <div className="akh-badges">
          <span className={`akh-badge akh-conf-${message.confidence}`}>
            {CONFIDENCE_LABEL[message.confidence as string] ?? message.confidence}
          </span>
          {routeLabel && <span className="akh-badge akh-route">{routeLabel}</span>}
          {message.abstained && <span className="akh-badge akh-abstain">No approved SOP found</span>}
        </div>
      )}

      {showCitations && (
        <ul className="akh-citations" aria-label="Citations">
          {message.citations!.map((c, i) => (
            <li key={`${c.ref}-${i}`} className={`akh-chip akh-chip-${c.quality}`} title={c.type}>
              {c.ref}
            </li>
          ))}
        </ul>
      )}

      {message.disclaimer && <p className="akh-disclaimer">{message.disclaimer}</p>}
    </div>
  );
}

export interface AskHeliosChatHandle {
  newChat: () => void;
}

/**
 * Structured Ask Helios chat surface. Non-streaming: each answer arrives as a
 * complete envelope, rendered with a confidence badge, citation chips, and the
 * advisory disclaimer. Distinct from ChatFullView (the streaming EMS chat).
 *
 * The "new chat" action lives in the panel header (the `+` icon), not in this
 * component's own markup, so it's exposed here via ref instead of a prop.
 */
const AskHeliosChat = forwardRef<AskHeliosChatHandle>(function AskHeliosChat(_props, ref) {
  const {
    messages,
    inputValue,
    setInputValue,
    isThinking,
    progressLabel,
    sessionId,
    messagesEndRef,
    handleSendMessage,
    handleNewChat,
    isEmpty,
    askQuestion,
    retryableQuestion,
  } = useAskHelios();

  useImperativeHandle(ref, () => ({ newChat: handleNewChat }), [handleNewChat]);

  const lastIndex = messages.length - 1;

  return (
    <div className="akh-chat">
      {/* Answers arrive without focus moving, so announce them politely. */}
      <div className="akh-messages" role="log" aria-live="polite" aria-relevant="additions text">
        {messages.map((m, i) => (
          <div key={i} className={`akh-msg akh-msg-${m.role}${m.error ? ' akh-msg-error' : ''}`}>
            <div className="akh-bubble">
              {m.role === 'assistant' ? (
                <ReactMarkdown>{m.content}</ReactMarkdown>
              ) : (
                m.content
              )}
            </div>
            {m.role === 'assistant' && <AssistantMeta message={m} />}
            {m.error && i === lastIndex && retryableQuestion && !isThinking && (
              <button
                type="button"
                className="akh-retry-btn"
                onClick={() => askQuestion(retryableQuestion)}
              >
                Retry
              </button>
            )}
          </div>
        ))}

        {isEmpty && !isThinking && (
          <div className="akh-suggestions">
            <span className="akh-suggestions-label">Try asking</span>
            {SUGGESTED_QUESTIONS.map(q => (
              <button
                key={q}
                type="button"
                className="akh-suggestion"
                onClick={() => askQuestion(q)}
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {isThinking && (
          <div className="akh-msg akh-msg-assistant">
            <div className="akh-bubble akh-thinking">
              {progressLabel ?? 'Consulting the SOP library…'}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <p className="akh-footer-disclaimer">
        Helios AI can make mistakes. Review its output before acting on it.
      </p>

      <ChatInput
        inputValue={inputValue}
        setInputValue={setInputValue}
        isThinking={isThinking}
        sessionId={sessionId}
        onSubmit={handleSendMessage}
        placeholder="Ask about an SOP or procedure..."
        hideSessionId
      />
    </div>
  );
});

export default AskHeliosChat;
